import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Container,
  Header,
  Button,
  Box,
  SpaceBetween,
  Toggle,
} from '@cloudscape-design/components';

import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  Controls,
  Background,
  MiniMap,
  NodeProps,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from 'reactflow';

import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { ApiPipeline } from '../../services/apiService';

interface PipelineDependencyGraphProps {
  pipeline: ApiPipeline;
  darkMode?: boolean;
}

type LayoutDirection = 'TB' | 'LR';

interface LayoutOptions {
  direction: LayoutDirection;
  nodeWidth: number;
  nodeHeight: number;
  rankSep: number;
  nodeSep: number;
}

/* --------------------------------------------------
   CUSTOM NODE COMPONENT (uses AWS service icons)
-----------------------------------------------------*/
const getServiceIcon = (type: string): string => {
  // Use public directory paths - these will be served by the React dev server
  const baseIconPath = '/assets/aws-icons/';

  // Add debug logging to see what types we're getting
  console.log('Service type:', type);

  switch (type) {
    case 'buffer.sqs':
      return `${baseIconPath}sqs.svg`;
    case 'processor.lambda':
      return `${baseIconPath}lambda.svg`;
    case 'buffer.kinesis':
      return `${baseIconPath}kinesis.svg`;
    case 'buffer.s3_firehose':
      return `${baseIconPath}firehose.svg`;
    case 'storage.s3':
      return `${baseIconPath}s3.svg`;
    case 'storage.s3tables':
      return `${baseIconPath}s3.svg`;
    case 'source.sns':
      return `${baseIconPath}sns.svg`;
    case 'processor.glue':
      return `${baseIconPath}glue.svg`;
    case 'processor.fanout':
      return `${baseIconPath}lambda.svg`;
    default:
      console.warn('Unknown service type:', type, '- using lambda icon as fallback');
      return `${baseIconPath}lambda.svg`; // fallback
  }
};

const PipelineComponentNode: React.FC<NodeProps> = ({ data, selected }) => {
  const isHorizontal = data.layoutDirection === 'LR';
  const hasIncomingConnections = data.hasIncomingConnections;
  const hasOutgoingConnections = data.hasOutgoingConnections;
  // Use dark mode flag passed through node data
  const isDark = data.isDarkMode === true;
  const textColor = isDark ? '#d5dbdb' : '#16191f';
  const handleColor = isDark ? '#aaa' : '#555';

  // Check if this node type should be clickable
  const isClickable = data.arn && data.type !== 'source.sns';

  // Generate AWS console URL from ARN
  const getAwsConsoleUrl = (arn: string): string => {
    if (!arn) return '#';

    try {
      const parts = arn.split(':');
      if (parts.length < 6) return '#';

      const [, , service, region] = parts;

      // Use console.aws.amazon.com/go/view which resolves ARNs to the correct console page
      switch (service) {
        case 'lambda':
        case 'sqs':
        case 'kinesis':
        case 'firehose':
        case 'glue':
          return `https://console.aws.amazon.com/go/view?arn=${encodeURIComponent(arn)}&region=${region}`;
        case 's3': {
          const resource = parts.slice(5).join(':');
          return `https://s3.console.aws.amazon.com/s3/buckets/${resource}?region=${region}`;
        }
        default:
          return `https://console.aws.amazon.com/console/home?region=${region}`;
      }
    } catch (error) {
      console.error('Error parsing ARN:', error);
      return '#';
    }
  };

  const handleClick = () => {
    if (isClickable) {
      const url = getAwsConsoleUrl(data.arn);
      if (url !== '#') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return (
    <div
      style={{
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '120px',
        maxWidth: '120px',
        cursor: isClickable ? 'pointer' : 'default',
      }}
      onClick={handleClick}
      title={isClickable ? `Click to open ${data.name} in AWS Console` : undefined}
    >
      {hasIncomingConnections && (
        <Handle
          type="target"
          position={isHorizontal ? Position.Left : Position.Top}
          style={{ background: handleColor }}
        />
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <img
          src={getServiceIcon(data.type)}
          alt={data.type}
          style={{
            width: '64px',
            height: '64px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
          }}
        />

        <div
          style={{
            maxWidth: '120px',
            wordWrap: 'break-word',
            lineHeight: '1.2',
            color: textColor,
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          {data.name}
        </div>
      </div>

      {hasOutgoingConnections && (
        <Handle
          type="source"
          position={isHorizontal ? Position.Right : Position.Bottom}
          style={{ background: handleColor }}
        />
      )}
    </div>
  );
};

const nodeTypes = { pipelineComponent: PipelineComponentNode };

/* --------------------------------------------------
   MAIN COMPONENT
-----------------------------------------------------*/
const PipelineDependencyGraph: React.FC<PipelineDependencyGraphProps> = ({
  pipeline,
  darkMode,
}) => {
  // Detect dark mode from prop or CloudScape's body class
  const isDarkMode =
    darkMode !== undefined
      ? darkMode
      : document.body.classList.contains('awsui-dark-mode') ||
        document.documentElement.classList.contains('awsui-dark-mode');
  const edgeStrokeColor = isDarkMode ? '#999' : '#666';
  /* --------------------------------------------------
       Dagre Layout Configuration
  -----------------------------------------------------*/
  const [layoutOptions, setLayoutOptions] = useState<LayoutOptions>({
    direction: 'LR', // Left-to-Right (horizontal) - now default
    nodeWidth: 120,
    nodeHeight: 100,
    rankSep: 100, // Separation between ranks (levels)
    nodeSep: 40, // Separation between nodes in same rank
  });

  /* --------------------------------------------------
       Dagre Automatic Layout
  -----------------------------------------------------*/
  const applyDagreLayout = (nodes: Node[], edges: Edge[], options: LayoutOptions) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Configure the graph layout
    dagreGraph.setGraph({
      rankdir: options.direction,
      nodesep: options.nodeSep,
      ranksep: options.rankSep,
      marginx: 20,
      marginy: 20,
    });

    // Add nodes to dagre graph
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, {
        width: options.nodeWidth,
        height: options.nodeHeight,
      });
    });

    // Add edges to dagre graph
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate layout
    dagre.layout(dagreGraph);

    // Apply calculated positions to nodes
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          // Dagre gives center coordinates, we need top-left
          x: nodeWithPosition.x - options.nodeWidth / 2,
          y: nodeWithPosition.y - options.nodeHeight / 2,
        },
      };
    });

    return layoutedNodes;
  };

  /* --------------------------------------------------
       Transform API into ReactFlow nodes & edges
       IMPORTANT: use node.id from API (not node.name)
  -----------------------------------------------------*/
  const initialData = useMemo(() => {
    const apiNodes = pipeline.nodes ?? [];
    const apiEdges = pipeline.edges ?? [];

    // ---- Nodes: use API node.id directly ----
    const nodes: Node[] = apiNodes.map((node: any, i: number) => {
      // Use API-provided `id`. Fallback to generated id only if missing.
      const id = node.id ?? `node-${i}`;

      // Check if this node has incoming or outgoing connections
      const hasIncomingConnections = apiEdges.some((edge: any) => {
        const target = edge.target ?? edge.to ?? edge.target_node ?? edge.targetNode;
        return target === id;
      });

      const hasOutgoingConnections = apiEdges.some((edge: any) => {
        const source = edge.source ?? edge.from ?? edge.source_node ?? edge.sourceNode;
        return source === id;
      });

      return {
        id,
        type: 'pipelineComponent',
        position: { x: 0, y: 0 },
        data: {
          ...node, // preserve type, arn, cost_metrics, usage_stats, etc.
          id,
          // Keep `name` for display (often different from id)
          name: node.name ?? node.id,
          layoutDirection: layoutOptions.direction, // Pass layout direction to node
          hasIncomingConnections,
          hasOutgoingConnections,
          isDarkMode, // Pass dark mode to node for text color
        },
      };
    });

    // ---- Edges: use API source/target directly ----
    const edges: Edge[] = (apiEdges || []).map((e: any, i: number) => {
      const rawSource = e.source ?? e.from ?? e.source_node ?? e.sourceNode;
      const rawTarget = e.target ?? e.to ?? e.target_node ?? e.targetNode;

      // Keep raw values as-is — they should match API node.id values.
      const source = rawSource;
      const target = rawTarget;

      console.log(`EDGE RAW ${i}:`, rawSource, '→', rawTarget);
      return {
        id: `edge-${i}`,
        source,
        target,
        type: 'smoothstep',
        animated: true,
        style: { stroke: edgeStrokeColor, strokeWidth: 2 },
      };
    });

    console.log(
      'NODES (API ids):',
      nodes.map((n) => n.id),
    );
    console.log(
      'EDGES (source → target):',
      edges.map((e) => `${e.source} → ${e.target}`),
    );

    const layoutedNodes = applyDagreLayout(nodes, edges, layoutOptions);
    return { nodes: layoutedNodes, edges };
  }, [pipeline, layoutOptions, isDarkMode, edgeStrokeColor]);

  /* --------------------------------------------------
       ReactFlow synced state
  -----------------------------------------------------*/
  const [nodes, setNodes, onNodesChange] = useNodesState(initialData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData.edges);

  // Keep state in sync when pipeline changes
  useEffect(() => {
    setNodes(initialData.nodes);
    setEdges(initialData.edges);
  }, [initialData.nodes, initialData.edges, setNodes, setEdges]);

  /* --------------------------------------------------
       Handlers
  -----------------------------------------------------*/
  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [],
  );

  const autoLayoutNodes = () => {
    setNodes(applyDagreLayout(nodes, edges, layoutOptions));
  };

  const handleLayoutToggle = (checked: boolean) => {
    const direction: LayoutDirection = checked ? 'TB' : 'LR'; // checked = vertical, unchecked = horizontal
    const newOptions = { ...layoutOptions, direction };
    setLayoutOptions(newOptions);

    // Update nodes with new layout direction and reapply layout
    const updatedNodes = nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        layoutDirection: direction,
      },
    }));

    setNodes(applyDagreLayout(updatedNodes, edges, newOptions));
  };

  /* --------------------------------------------------
       RENDER
  -----------------------------------------------------*/
  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <SpaceBetween direction="horizontal" size="xs">
                <Box fontSize="body-s" color="text-body-secondary">
                  Horizontal
                </Box>
                <Toggle
                  onChange={({ detail }) => handleLayoutToggle(detail.checked)}
                  checked={layoutOptions.direction === 'TB'}
                  ariaLabel="Toggle layout direction"
                />
                <Box fontSize="body-s" color="text-body-secondary">
                  Vertical
                </Box>
              </SpaceBetween>
              <Button iconName="refresh" onClick={autoLayoutNodes}>
                Re-layout
              </Button>
            </SpaceBetween>
          }
        >
          Pipeline Architecture
        </Header>
      }
    >
      <div style={{ width: '100%', height: '600px' }} className={isDarkMode ? 'dark' : ''}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
        >
          <Controls />
          <Background />
          <MiniMap
            nodeColor={(node: Node) => {
              // color MiniMap nodes by AWS service type
              switch (node.data.type) {
                case 'source.sns':
                  return '#d13212'; // red - SNS
                case 'buffer.sqs':
                  return '#d13212'; // red - SQS
                case 'processor.lambda':
                case 'processor.fanout':
                  return '#ff9900'; // orange - Lambda
                case 'buffer.kinesis':
                  return '#8c4fff'; // purple - Kinesis
                case 'buffer.s3_firehose':
                  return '#8c4fff'; // purple - Firehose
                case 'storage.s3':
                case 'storage.s3tables':
                  return '#1b660f'; // green - S3
                default:
                  return '#7f7f7f';
              }
            }}
            nodeStrokeWidth={3}
            pannable
            zoomable
          />
        </ReactFlow>
      </div>
    </Container>
  );
};

export default PipelineDependencyGraph;
